<script lang="ts" setup>
import * as R from 'ramda'

import { MultiSelect } from '@collector/shared-ui'
import { BaseballIncidentId } from '@collector/sportsapi-client-legacy'
import { useIncidents } from '@desktop/views/Relation/composables'
import { useBaseballParticipants } from '@desktop/views/Relation/Sports/Baseball/composables'
import { usePitchIncidentsBlockades } from '@desktop/views/Relation/Sports/composables'
import {
  IncidentPlayerOption,
  IncidentPlayerOptionValue,
} from '@desktop/views/Relation/types'

import { BaseballBaseIncidentId, getBaseOffIncidentId } from './utils'

interface Props {
  baseIncidentId: BaseballBaseIncidentId
  options: IncidentPlayerOption[]
}

const props = defineProps<Props>()

// emits
type Emits = {
  change: [
    [
      baseIncidentId: BaseballBaseIncidentId,
      playerValue: IncidentPlayerOptionValue,
    ],
  ]
}

const emit = defineEmits<Emits>()

// state
const model = defineModel<IncidentPlayerOptionValue>()

// composables
const { battingParticipant } = useBaseballParticipants()
const { incidents } = useIncidents()
const { isEveryPitchIncidentBlocked } = usePitchIncidentsBlockades()

// methods
function initModelValue(): IncidentPlayerOptionValue | undefined {
  const baseOffIncidentId = getBaseOffIncidentId(props.baseIncidentId)

  const baseClearIncidentIndex = R.findLastIndex(
    (incident) =>
      (incident.incident_id === baseOffIncidentId &&
        incident.participant_team_id === battingParticipant.value?.id) ||
      incident.incident_id === BaseballIncidentId.ResetStats,
    incidents.value,
  )

  const baseOnIncidentIndex = R.findLastIndex(
    (incident) =>
      incident.incident_id === props.baseIncidentId &&
      incident.participant_team_id === battingParticipant.value?.id &&
      !!incident.participant_name,
    incidents.value,
  )

  // If the "on" incident is after the "clear" incident, the base is occupied by that player
  if (baseOnIncidentIndex > baseClearIncidentIndex) {
    const baseOnIncident = incidents.value[baseOnIncidentIndex]

    return {
      id: baseOnIncident.participant_id,
      shortName: baseOnIncident.participant_name,
      teamId: baseOnIncident.participant_team_id.toString(),
    }
  }

  return undefined
}

// methods
function onPlayerChange(value: IncidentPlayerOptionValue): void {
  if (R.equals(model.value, value)) {
    return
  }

  emit('change', [props.baseIncidentId, value])
  model.value = value
}

// init
model.value = initModelValue()
</script>

<template>
  <div class="flex">
    <slot />
    <MultiSelect
      class="w-full"
      withFilter
      withClear="option"
      :options
      :disabled="isEveryPitchIncidentBlocked"
      :modelValue="model"
      @update:modelValue="
        (value) => onPlayerChange(value as IncidentPlayerOptionValue)
      "
    />
  </div>
</template>
